import { useCallback, useEffect, useState } from 'react';

export interface ColumnsState<Column extends string> {
  visible: Column[];
  isVisible: (column: Column) => boolean;
  toggle: (column: Column, visible: boolean) => void;
  reset: () => void;
  isDefault: boolean;
}

export interface ColumnsOptions<Column extends string> {
  /** Every optional column, in the order the table renders them. */
  columns: readonly Column[];
  /** What a reader sees before touching anything. */
  defaults: readonly Column[];
  storageKey: string;
}

const sameSet = <Column extends string>(a: Column[], b: readonly Column[]): boolean =>
  a.length === b.length && a.every((column) => b.includes(column));

/**
 * Which columns a list shows, remembered per browser — the choice is a personal
 * preference, like the theme, not project data. Each list passes its own set
 * and storage key; the behaviour is identical, which is why it lives here.
 */
export const createColumnsHook = <Column extends string>({
  columns,
  defaults,
  storageKey,
}: ColumnsOptions<Column>): (() => ColumnsState<Column>) => {
  const isColumn = (value: unknown): value is Column =>
    typeof value === 'string' && (columns as readonly string[]).includes(value);

  const read = (): Column[] => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === null) return [...defaults];
      const parsed: unknown = JSON.parse(stored);
      // An empty selection is a valid choice (key and title remain); anything
      // unreadable falls back to the defaults.
      return Array.isArray(parsed) ? parsed.filter(isColumn) : [...defaults];
    } catch {
      return [...defaults];
    }
  };

  return () => {
    const [visible, setVisible] = useState<Column[]>(read);

    useEffect(() => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(visible));
      } catch {
        // A browser that refuses storage still gets a working list.
      }
    }, [visible]);

    const toggle = useCallback((column: Column, next: boolean) => {
      setVisible((current) =>
        next
          ? columns.filter((value) => value === column || current.includes(value))
          : current.filter((value) => value !== column),
      );
    }, []);

    const reset = useCallback(() => setVisible([...defaults]), []);

    return {
      visible,
      isVisible: (column) => visible.includes(column),
      toggle,
      reset,
      isDefault: sameSet(visible, defaults),
    };
  };
};
